import {
  TAllAttendanceListDataDB,
  TAllAttendanceListDataUI,
  TAttendanceUpdateUI,
  TEventAttendanceListDataDB,
  TEventAttendanceListDataUI,
  TEventAttendanceUpdateUI,
  TGetAllAttendanceList,
  TGetChildCareDataDB,
  TGetChildCareDataUI,
  TGetChildCareList,
  TGetEventChildCareDataDB,
  TGetEventChildCareDataUI,
  TGetEventChildCareList,
  TSessionAttendanceListDataDB,
  TSessionAttendanceListDataUI,
} from 'data/types/session.types';
import { ERegistrantsUrls } from 'data/urls/registrants.url';
import {
  getAllAttendanceListDataConverter,
  getChildCareListDataConverter,
  getEventAttendanceListDataConverter,
  getEventChildCareListDataConverter,
  getSessionAttendanceListDataConverter,
  updateAttendanceConverter,
  updateEventAttendanceConverter,
} from 'data/convertors/session.convertor';
import { emptySplitApi } from '../helpers/slice.helpers';

const baseUrl = `${ERegistrantsUrls.HOST_APP}/${ERegistrantsUrls.WORKSHOPS}`;
const eventsBaseUrl = `${ERegistrantsUrls.HOST_APP}/${ERegistrantsUrls.EVENTS}`;

export const registrantsApi = emptySplitApi('registrantsApi', [
  'session-attendance-list',
  'all-attendance-list',
  'child-care-list',
  'event-attendance-list',
  'event-child-care-list',
]).injectEndpoints({
  endpoints: build => ({
    getSessionAttendanceList: build.query<TSessionAttendanceListDataUI, string>(
      {
        query(sessionId) {
          return {
            url: `${baseUrl}/${ERegistrantsUrls.SESSIONS}/${sessionId}`,
            method: 'GET',
          };
        },
        providesTags: ['session-attendance-list'],
        transformResponse: (data: TSessionAttendanceListDataDB) =>
          getSessionAttendanceListDataConverter.fromDb(data),
      },
    ),
    getAllAttendanceList: build.query<
      TAllAttendanceListDataUI,
      TGetAllAttendanceList
    >({
      query(params) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['all-attendance-list'],
      transformResponse: (data: TAllAttendanceListDataDB) =>
        getAllAttendanceListDataConverter.fromDb(data),
    }),
    getChildCareList: build.query<TGetChildCareDataUI, TGetChildCareList>({
      query(workshopId) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.CHILDCARE}`,
          method: 'GET',
          params: workshopId,
        };
      },
      providesTags: ['child-care-list'],
      transformResponse: (data: TGetChildCareDataDB) =>
        getChildCareListDataConverter.fromDb(data),
    }),
    updateAttendance: build.mutation<void, TAttendanceUpdateUI>({
      query(body) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.ATTENDANCE}`,
          method: 'PATCH',
          body: updateAttendanceConverter.toDb(body),
        };
      },
      invalidatesTags: ['session-attendance-list', 'all-attendance-list'],
    }),
    // Events
    getEventAttendanceList: build.query<
      TEventAttendanceListDataUI,
      { eventId: string | null; date: string | null }
    >({
      query(params) {
        return {
          url: `${eventsBaseUrl}/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['event-attendance-list'],
      transformResponse: (data: TEventAttendanceListDataDB) =>
        getEventAttendanceListDataConverter.fromDb(data),
    }),
    updateEventAttendance: build.mutation<void, TEventAttendanceUpdateUI>({
      query(body) {
        return {
          url: `${eventsBaseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.ATTENDANCE}`,
          method: 'PATCH',
          body: updateEventAttendanceConverter.toDb(body),
        };
      },
      invalidatesTags: ['event-attendance-list'],
    }),
    getEventChildCareList: build.query<
      TGetEventChildCareDataUI,
      TGetEventChildCareList
    >({
      query(params) {
        return {
          url: `${eventsBaseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.CHILDCARE}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['event-child-care-list'],
      transformResponse: (data: TGetEventChildCareDataDB) =>
        getEventChildCareListDataConverter.fromDb(data),
    }),
  }),
});

export const {
  useGetSessionAttendanceListQuery,
  useGetAllAttendanceListQuery,
  useGetChildCareListQuery,
  useUpdateAttendanceMutation,
  useGetEventAttendanceListQuery,
  useUpdateEventAttendanceMutation,
  useGetEventChildCareListQuery,
} = registrantsApi;
